'use client';
import React from 'react';
import { cn } from '../../utils/cn';

export interface SupportCardProps {
  title: string;
  phone?: string;
  phoneLabel?: string;
  email?: string;
  emailLabel?: string;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  variant?: 'default' | 'bordered';
}

const SupportCard: React.FC<SupportCardProps> = ({
  title,
  phone,
  phoneLabel = 'Phone',
  email,
  emailLabel = 'Email',
  icon,
  className,
  onClick,
  variant = 'default',
}) => {
  const cardClasses = cn(
    'flex grow flex-col gap-6 text-velux-black p-8 sm:max-w-80 transition-all',
    variant === 'bordered' && 'border border-velux-ui-grey rounded-lg',
    onClick && 'cursor-pointer hover:shadow-lg',
    className
  );

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={e => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Header with icon and title */}
      <div className="flex items-start gap-4">
        {icon && <div className="flex-shrink-0 text-velux-professional-red">{icon}</div>}
        <div>
          <h2 className="text-xl font-bold text-velux-black">{title}</h2>
        </div>
      </div>

      {/* Contact information */}
      <div className="space-y-4">
        {phone && (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-velux-grey uppercase tracking-wide">
              {phoneLabel}:
            </p>
            <a
              href={`tel:${phone}`}
              className="text-velux-professional-red hover:text-velux-red font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {phone}
            </a>
          </div>
        )}

        {email && (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-velux-grey uppercase tracking-wide">
              {emailLabel}:
            </p>
            <a
              href={`mailto:${email}`}
              className="text-velux-professional-red hover:text-velux-red font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {email}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

SupportCard.displayName = 'SupportCard';

export { SupportCard };